import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import LoveCalculator from './components/LoveCalculator';
import DateCalculator from './components/DateCalculator';
import Tips from './components/Tips';
import SponsorSection from './components/SponsorSection';
import Footer from './components/Footer';
import BackgroundHearts from './components/BackgroundHearts';
import ScrollToTop from './components/ScrollToTop';
import PageHeading from './components/PageHeading';
import { useLanguage } from './hooks/useLanguage';
import { useDarkMode } from './hooks/useDarkMode';
import { useSEO } from './hooks/useSEO';
import { parseUrlParams } from './utils/shareUtils';
import './App.css';

const AboutUs = lazy(() => import('./pages/AboutUs'));
const Features = lazy(() => import('./pages/Features'));
const Help = lazy(() => import('./pages/Help'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const NotFound = lazy(() => import('./pages/NotFound'));

const legacyHashRoutes = new Set([
  '/about',
  '/features',
  '/help',
  '/contact',
  '/privacy',
  '/terms'
]);

if (typeof window !== 'undefined') {
  const legacyHashPath = window.location.hash.slice(1);
  const legacyUrl = new URL(legacyHashPath || '/', window.location.origin);

  if (legacyHashRoutes.has(legacyUrl.pathname)) {
    window.history.replaceState(
      window.history.state,
      '',
      `${legacyUrl.pathname}${legacyUrl.search}`
    );
  }
}

function SEOController({ language, activeTab, calculatorData }) {
  const { pathname } = useLocation();

  useSEO({ pathname, language, activeTab, calculatorData });

  return null;
}

function App() {
  const [activeTab, setActiveTab] = useState('love');
  const { language, setLanguage } = useLanguage();
  const { isDark } = useDarkMode();
  const [calculatorData, setCalculatorData] = useState({});

  useEffect(() => {
    const { language: urlLanguage } = parseUrlParams();
    if (urlLanguage) setLanguage(urlLanguage);
  }, []);

  const handleCalculatorDataUpdate = (data) => {
    setCalculatorData(data);
  };

  const LoadingFallback = () => (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-love-500"></div>
    </div>
  );

  return (
    <Router>
      <SEOController
        language={language}
        activeTab={activeTab}
        calculatorData={calculatorData}
      />
      <ScrollToTop />
      <div className={`min-h-screen relative bg-love-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300 ${isDark ? 'dark' : ''}`}>
        <BackgroundHearts />
        <div className="max-w-3xl mx-auto p-4 md:p-6 relative z-10">
          <Header language={language} setLanguage={setLanguage} />

          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <PageHeading className="sr-only">
                      {language === 'zh' ? '戀愛紀念日與日期計算器' : 'Love Anniversary and Date Calculator'}
                    </PageHeading>
                    <motion.div
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6 relative z-10"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="bg-gradient-to-r from-love-50 to-secondary-50 dark:from-gray-750 dark:to-gray-800 border-b border-love-100 dark:border-gray-700">
                        <nav className="flex px-6" role="tablist" aria-label={language === 'zh' ? '計算器切換' : 'Calculator tabs'}>
                          <button
                            onClick={() => setActiveTab('love')}
                            className={`py-4 px-4 text-center font-medium text-sm flex-1 transition-all duration-300 ${
                              activeTab === 'love'
                                ? 'text-love-500 border-b-2 border-love-500'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                            role="tab"
                            aria-selected={activeTab === 'love'}
                            aria-controls="panel-love"
                          >
                            {language === 'zh' ? '我們的愛情' : 'Our Love'}
                          </button>
                          <button
                            onClick={() => setActiveTab('standard')}
                            className={`py-4 px-4 text-center font-medium text-sm flex-1 transition-all duration-300 ${
                              activeTab === 'standard'
                                ? 'text-love-500 border-b-2 border-love-500'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                            role="tab"
                            aria-selected={activeTab === 'standard'}
                            aria-controls="panel-standard"
                          >
                            {language === 'zh' ? '日期計算' : 'Date Calculator'}
                          </button>
                        </nav>
                      </div>

                      <AnimatePresence mode="wait">
                        {activeTab === 'love' ? (
                          <motion.div
                            key="love"
                            id="panel-love"
                            role="tabpanel"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <LoveCalculator language={language} onDataUpdate={handleCalculatorDataUpdate} />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="standard"
                            id="panel-standard"
                            role="tabpanel"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <DateCalculator language={language} onDataUpdate={handleCalculatorDataUpdate} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </>
                }
              />
              <Route path="/about" element={<AboutUs language={language} />} />
              <Route path="/features" element={<Features language={language} />} />
              <Route path="/help" element={<Help language={language} />} />
              <Route path="/contact" element={<Contact language={language} />} />
              <Route path="/privacy" element={<PrivacyPolicy language={language} />} />
              <Route path="/terms" element={<TermsOfService language={language} />} />
              <Route path="*" element={<NotFound language={language} />} />
            </Routes>
          </Suspense>

          <Tips language={language} />
          <SponsorSection language={language} />
          <Footer language={language} />
        </div>
      </div>
    </Router>
  );
}

export default App;